import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpTrayIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import BillToLookupField, { type BillToValue } from '../../components/BillToLookupField';
import CustomerLookupField, { type CustomerValue } from '../../components/CustomerLookupField';
import orderUpdateImportService, {
  type AppendOption,
  type EnqueueResponse,
  type ImportJobStatus,
  type ReferenceData,
  type RowError,
  TERMINAL_STATUSES,
} from '../../services/orderUpdateImport.service';

const EMPTY_BILLTO: BillToValue = { recno: 0, name: '' };

/**
 * Order Update via Excel upload — 360 port of v1 imports/orderupdate.pl.
 *
 * Pick a Customer OR Bill To, choose which fields to update, download a blank
 * spreadsheet, fill + upload it. The upload is validated + enqueued server-side
 * (202) and processed asynchronously by the Sync worker; this page polls the
 * job for progress and shows the per-PRO results log.
 */
const OrderUpdateImportPage: React.FC = () => {
  const [ref, setRef] = useState<ReferenceData | null>(null);
  const [customer, setCustomer] = useState<CustomerValue>({ code: 0, name: '' });
  const [billto, setBillto] = useState<BillToValue>(EMPTY_BILLTO);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [appendOption, setAppendOption] = useState<AppendOption>('APPEND');
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [job, setJob] = useState<ImportJobStatus | null>(null);

  useEffect(() => {
    orderUpdateImportService.getReferenceData().then(setRef).catch(() => setRef(null));
  }, []);

  const selectedLabels = useMemo(
    () => (ref?.fields ?? []).filter((f) => selected[f.label]).map((f) => f.label),
    [ref, selected],
  );

  const scope = customer.code > 0 ? 'customer' : billto.recno > 0 ? 'billto' : null;

  // --- selection handlers ---------------------------------------------------

  const toggleField = (label: string) => {
    setSelected((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      // v1: LBS and KGS are mutually exclusive.
      if (next[label] && label === 'LBS') next.KGS = false;
      if (next[label] && label === 'KGS') next.LBS = false;
      return next;
    });
  };

  // Customer OR Bill To — selecting one clears the other.
  const onChangeCustomer = (v: CustomerValue) => {
    setCustomer(v);
    if (v.code > 0) setBillto(EMPTY_BILLTO);
  };
  const onChangeBillto = (v: BillToValue) => {
    setBillto(v);
    if (v.recno > 0) setCustomer({ code: 0, name: '' });
  };

  // --- actions --------------------------------------------------------------

  const onDownloadTemplate = async () => {
    setErrorMsg(null);
    try {
      await orderUpdateImportService.downloadTemplate(selectedLabels);
    } catch (e) {
      setErrorMsg(extractDetail(e));
    }
  };

  const onImport = async () => {
    setErrorMsg(null);
    setRowErrors([]);
    if (!scope) { setErrorMsg('Select a Customer or a Bill To.'); return; }
    if (selectedLabels.length === 0) { setErrorMsg('Select at least one field to update.'); return; }
    if (!file) { setErrorMsg('Choose a file to import.'); return; }
    setBusy(true);
    setJob(null);
    setJobUuid(null);
    try {
      const res: EnqueueResponse = await orderUpdateImportService.apply({
        file,
        scopeType: scope,
        customercode: scope === 'customer' ? customer.code : null,
        billto: scope === 'billto' ? billto.recno : null,
        appendOption,
        fields: selectedLabels,
      });
      setJobUuid(res.job_uuid);
      // fetch the first status snapshot immediately
      orderUpdateImportService.getStatus(res.job_uuid).then(setJob).catch(() => {});
    } catch (e) {
      const errs = extractRowErrors(e);
      if (errs.length) setRowErrors(errs);
      else setErrorMsg(extractDetail(e));
    } finally {
      setBusy(false);
    }
  };

  // --- polling --------------------------------------------------------------

  const isTerminal = job != null && TERMINAL_STATUSES.includes(job.status);
  const pollDelay = jobUuid && !isTerminal ? 1500 : null;

  const refresh = useCallback(() => {
    if (!jobUuid) return;
    orderUpdateImportService.getStatus(jobUuid).then(setJob).catch(() => {});
  }, [jobUuid]);
  useVisibilityAwareInterval(refresh, pollDelay);

  const resetRun = () => {
    setJob(null);
    setJobUuid(null);
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  // --- render ---------------------------------------------------------------

  return (
    <div className="p-[18px] max-w-[860px]">
      <div className="flex items-center gap-1.5 mb-3">
        <ArrowUpTrayIcon className="h-5 w-5 text-blue-600" />
        <h1 className="m-0 text-[0.9375rem] font-bold text-slate-900 dark:text-gray-100">Order Update via Excel</h1>
      </div>

      {/* Scope: Customer OR Bill To */}
      <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg p-4 mb-3">
        <div className="text-[0.75rem] font-bold text-slate-700 dark:text-gray-200 mb-2">Customer <span className="font-normal text-slate-400">or</span> Bill To</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CustomerLookupField value={customer} onChange={onChangeCustomer} />
          <BillToLookupField value={billto} onChange={onChangeBillto} />
        </div>
      </section>

      {/* Fields to update */}
      <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg p-4 mb-3">
        <div className="text-[0.75rem] font-bold text-slate-700 dark:text-gray-200 mb-2">Fields to Update</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">
          {(ref?.fields ?? []).map((f) => (
            <label key={f.label} className="flex items-center gap-1.5 text-[0.75rem] text-slate-700 dark:text-gray-200 cursor-pointer">
              <input type="checkbox" checked={!!selected[f.label]} onChange={() => toggleField(f.label)} />
              {f.appendable && <span className="text-blue-600" title="Appendable">*</span>}
              {f.label}
            </label>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-slate-100 dark:border-gray-800">
          <label className="text-[0.75rem] text-slate-600 dark:text-gray-300">
            Fields marked <span className="text-blue-600">*</span> are appendable:
            <select
              value={appendOption}
              onChange={(e) => setAppendOption(e.target.value as AppendOption)}
              className="ml-2 text-[0.75rem] border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 bg-panel dark:text-gray-100"
            >
              <option value="APPEND">APPEND</option>
              <option value="UPDATE">UPDATE</option>
            </select>
          </label>
          <button
            type="button"
            onClick={onDownloadTemplate}
            disabled={selectedLabels.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[0.75rem] font-semibold text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700 rounded disabled:opacity-40 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <ArrowDownTrayIcon className="h-4 w-4" /> Create Blank Spreadsheet
          </button>
        </div>
      </section>

      {/* Upload */}
      <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg p-4 mb-3">
        <div className="text-[0.75rem] font-bold text-slate-700 dark:text-gray-200 mb-2">Upload &amp; Import</div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            aria-label="Order update spreadsheet"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setRowErrors([]); setErrorMsg(null); }}
            className="text-[0.75rem] text-slate-700 dark:text-gray-200"
          />
          <button
            type="button"
            onClick={onImport}
            disabled={busy || !file || !scope || selectedLabels.length === 0}
            className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded disabled:opacity-40 hover:bg-blue-700"
          >
            {busy ? 'Uploading…' : 'Import'}
          </button>
          <span className="text-[0.6875rem] text-slate-400">.xlsx or .xls</span>
        </div>

        {errorMsg && (
          <div className="mt-3 px-3 py-2 text-[0.75rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            {errorMsg}
          </div>
        )}
        {rowErrors.length > 0 && (
          <div className="mt-3 px-3 py-2 text-[0.75rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <div className="font-semibold mb-1">The file was not imported. Fix these and try again:</div>
            <ul className="list-disc ml-5 space-y-0.5">
              {rowErrors.map((e, i) => (
                <li key={i}>{e.row ? `Row ${e.row}: ` : ''}{e.message}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Progress + results */}
      {job && <JobPanel job={job} onReset={isTerminal ? resetRun : undefined} />}
    </div>
  );
};

// --- job progress + results log --------------------------------------------

const JobPanel: React.FC<{ job: ImportJobStatus; onReset?: () => void }> = ({ job, onReset }) => {
  const pct = job.total_rows ? Math.round((job.processed_rows / job.total_rows) * 100) : 0;
  const running = !TERMINAL_STATUSES.includes(job.status);
  return (
    <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">
          {running ? 'Processing…' : 'Import complete'} <span className="font-normal text-slate-400">({job.status})</span>
        </div>
        {onReset && (
          <button type="button" onClick={onReset} className="text-[0.75rem] font-medium text-blue-600 hover:underline">
            Start another import
          </button>
        )}
      </div>

      <div className="h-2 w-full bg-slate-100 dark:bg-gray-800 rounded overflow-hidden mb-2">
        <div className="h-full bg-blue-600 transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-3 text-[0.75rem] text-slate-600 dark:text-gray-300 mb-3">
        <span>{job.processed_rows}/{job.total_rows} processed</span>
        <span className="text-emerald-600">Updated: <b>{job.succeeded_rows}</b></span>
        <span className="text-amber-600">Skipped: <b>{job.skipped_rows}</b></span>
        <span className="text-red-600">Errors: <b>{job.failed_rows}</b></span>
      </div>

      {job.rows.length > 0 && (
        <div className="max-h-[360px] overflow-auto border border-slate-100 dark:border-gray-800 rounded">
          <table className="w-full text-[0.6875rem]">
            <thead className="sticky top-0 bg-slate-50 dark:bg-gray-800 text-slate-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-2 py-1 font-semibold">Row</th>
                <th className="text-left px-2 py-1 font-semibold">PRO</th>
                <th className="text-left px-2 py-1 font-semibold">Status</th>
                <th className="text-left px-2 py-1 font-semibold">Detail</th>
              </tr>
            </thead>
            <tbody>
              {job.rows.map((r) => (
                <tr key={r.row_number} className="border-t border-slate-100 dark:border-gray-800">
                  <td className="px-2 py-1 text-slate-500">{r.row_number}</td>
                  <td className="px-2 py-1 font-medium text-slate-800 dark:text-gray-200">{r.pronumber}</td>
                  <td className={`px-2 py-1 font-medium ${statusColor(r.status)}`}>{r.result_code || r.status}</td>
                  <td className="px-2 py-1 text-slate-600 dark:text-gray-400">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

function statusColor(status: string): string {
  if (status === 'ok') return 'text-emerald-600';
  if (status === 'error') return 'text-red-600';
  if (status === 'skipped') return 'text-amber-600';
  return 'text-slate-500';
}

function extractDetail(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (e instanceof Error) return e.message;
  return String(e);
}

function extractRowErrors(e: unknown): RowError[] {
  const detail = (e as { response?: { data?: { detail?: { errors?: RowError[] } } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) return detail.errors;
  return [];
}

export default OrderUpdateImportPage;
